import { SWRFetcher } from '@commenda/utils';
import { useMemo } from 'react';
import useSWR from 'swr';
import { API_BASE_PATH } from './constants';
import {
  IAdminUpcomingFilingsResponse,
  IAdminUpcomingFilingsRow,
  UseAdminUpcomingFilingsOptions,
} from './types';

const LIST_PATH = `${API_BASE_PATH}/list`;

function buildBody(opts: UseAdminUpcomingFilingsOptions): Record<string, unknown> {
  const body: Record<string, unknown> = {};

  if (opts.dueDateStart) body.dueDateStart = opts.dueDateStart;
  if (opts.dueDateEnd) body.dueDateEnd = opts.dueDateEnd;

  if (opts.filingTypeKeys?.length) body.filingTypeKeys = opts.filingTypeKeys;
  if (opts.categories?.length) body.categories = opts.categories;
  if (opts.includeBookkeeping !== undefined)
    body.includeBookkeeping = opts.includeBookkeeping;
  if (opts.countries?.length) body.countries = opts.countries;
  if (opts.statuses?.length) body.statuses = opts.statuses;
  if (opts.includeItemsWithoutServiceRequest !== undefined)
    body.includeItemsWithoutServiceRequest = opts.includeItemsWithoutServiceRequest;
  if (opts.showOnlyWhitelistedFilings !== undefined)
    body.showOnlyWhitelistedFilings = opts.showOnlyWhitelistedFilings;
  if (opts.managedBy?.length) body.managedBy = opts.managedBy;
  if (opts.agentFirmIds?.length) body.agentFirmIds = opts.agentFirmIds;
  if (opts.csmUserIds?.length) body.csmUserIds = opts.csmUserIds;
  if (opts.customerCompanyIds?.length)
    body.customerCompanyIds = opts.customerCompanyIds;
  if (opts.corporationIds?.length) body.corporationIds = opts.corporationIds;

  return body;
}

export function useAdminUpcomingFilings(options: UseAdminUpcomingFilingsOptions = {}) {
  const { enabled = true, ...filters } = options;
  const body = buildBody(filters);
  const cacheKey = enabled ? `${LIST_PATH}::${JSON.stringify(body)}` : null;

  const { data, error, mutate, isLoading, isValidating } =
    useSWR<IAdminUpcomingFilingsResponse>(
      cacheKey,
      cacheKey
        ? () => SWRFetcher({ method: 'POST', body }, undefined)(LIST_PATH)
        : null
    );

  const rows = useMemo<IAdminUpcomingFilingsRow[]>(() => data?.rows ?? [], [data]);

  return {
    rows,
    count: data?.count ?? 0,
    truncated: data?.truncated ?? false,
    error,
    mutate,
    isLoading: !!isLoading,
    isValidating: !!isValidating,
  };
}
